import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listQuotesApi } from "../lib/api.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";
import { formatCzk, formatDate } from "../lib/format.js";
import { DocumentIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * /quotes — historie všech vytvořených nabídek.
 */
export function QuotesListPage(): JSX.Element {
  const [data, setData] = useState<
    Awaited<ReturnType<typeof listQuotesApi>> | null
  >(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listQuotesApi()
      .then((d) => !cancelled && setData(d))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
        Nabídky
      </h1>
      <p className="mt-1 text-zinc-500">
        Historie vytvořených nabídek KTM Vlašim.
      </p>

      <div className="mt-8 bg-white rounded-2xl border border-zinc-200 shadow-card overflow-hidden">
        {loading ? (
          <LoadingBlock label="Načítám nabídky…" />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            icon={<DocumentIcon size={24} />}
            title="Žádné nabídky"
            description="Přidávejte díly do nabídky přes detail dílu nebo výkres."
          />
        ) : (
          <ul className="divide-y divide-zinc-100">
            {data.items.map((q) => (
              <li key={q.id}>
                <Link
                  to={`/quote/${q.id}`}
                  className="group flex items-center gap-4 px-5 py-4 hover:bg-zinc-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-ktm-orange-50 text-ktm-orange-600 flex items-center justify-center flex-shrink-0">
                    <DocumentIcon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-900 group-hover:text-ktm-orange transition-colors">
                        {q.number}
                      </span>
                      <Badge tone="neutral">{q.status}</Badge>
                    </div>
                    <div className="mt-0.5 text-sm text-zinc-500 truncate">
                      {q.customerName ?? "—"} · {q.itemCount} {q.itemCount === 1 ? "položka" : "položek"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">
                      {formatCzk(parseFloat(q.totalGrossCzk))}
                    </div>
                    <div className="text-[11px] text-zinc-400">
                      {formatDate(q.createdAt)}
                    </div>
                  </div>
                  <ChevronRightIcon
                    size={16}
                    className="text-zinc-300 group-hover:text-ktm-orange group-hover:translate-x-0.5 transition-all"
                  />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
